import { Injectable } from '@angular/core';
import { Observable, Subject, BehaviorSubject, timer, of } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { API_PATH } from 'src/environments/environment';
import { ContactReminder } from 'src/app/domain/IContactActivity';
import { ChatMessage } from '../../components/chat-widget/chat-message.model';

interface UserIds {
    userId?: string;
    customerId?: string;
    partnerId?: string;
}

interface ConnectionState {
    connected: boolean;
    connecting: boolean;
    error: string | null;
    lastConnected: Date | null;
}

@Injectable({
    providedIn: 'root',
})
export class WebsocketService {
    private socket: Socket | null = null;
    private notificationSubject = new Subject<any>();
    private reminderSubject = new Subject<ContactReminder>();
    private chatMessageSubject = new Subject<ChatMessage>();
    private connectionStateSubject = new BehaviorSubject<ConnectionState>({
        connected: false,
        connecting: false,
        error: null,
        lastConnected: null
    });

    private currentIds: UserIds | undefined;
    private referenceCount = 0;
    private connectionPromise: Promise<void> | null = null;
    private reconnectAttempts = 0;
    private maxReconnectAttempts = 5;
    private reconnectDelay = 1000;
    private maxReconnectDelay = 30000;
    private connectionTimeout = 15000;
    private heartbeatInterval: any = null;
    private cleanupTimeout: any = null;
    private isDestroyed = false;
    private connectionLock = false; // Lock para evitar conexões simultâneas
    private lastConnectionAttempt = 0; // Timestamp da última tentativa
    private connectionDebounceTime = 2000; // 2 segundos de debounce

    constructor() {
        // Limpeza automática quando a página é fechada
        window.addEventListener('beforeunload', () => {
            this.forceDisconnect();
        });
    }

    /**
     * Configura a conexão WebSocket
     */
    setupSocketConnection(ids: UserIds): Promise<void> {
        if (this.isDestroyed) {
            return Promise.reject(new Error('WebSocket service foi destruído'));
        }

        // Verificação rigorosa de autenticação
        if (!this.isUserAuthenticated(ids)) {
            console.warn('Usuário não autenticado, não conectando WebSocket');
            return Promise.reject(new Error('Usuário não autenticado'));
        }

        // Verificar debounce para evitar múltiplas tentativas rápidas
        const now = Date.now();
        if (now - this.lastConnectionAttempt < this.connectionDebounceTime) {
            console.log('Tentativa de conexão muito rápida, aguardando debounce...');
            return new Promise((resolve, reject) => {
                setTimeout(() => {
                    this.setupSocketConnection(ids).then(resolve).catch(reject);
                }, this.connectionDebounceTime - (now - this.lastConnectionAttempt));
            });
        }
        this.lastConnectionAttempt = now;

        // Se já existe uma promise de conexão em andamento, aguarda
        if (this.connectionPromise) {
            console.log('Conexão já em andamento, aguardando...');
            return this.connectionPromise;
        }

        // Verifica se já está conectado com os mesmos IDs
        if (this.isConnectedWithSameIds(ids)) {
            this.referenceCount++;
            console.log(`Conexão já existe com os mesmos IDs. Referências: ${this.referenceCount}`);
            return Promise.resolve();
        }

        // Verifica se há uma conexão em andamento
        if (this.connectionLock) {
            console.log('Conexão bloqueada, aguardando...');
            return new Promise((resolve, reject) => {
                const checkConnection = () => {
                    if (!this.connectionLock) {
                        this.setupSocketConnection(ids).then(resolve).catch(reject);
                    } else {
                        setTimeout(checkConnection, 100);
                    }
                };
                checkConnection();
            });
        }

        this.connectionLock = true;
        this.connectionPromise = this.createConnection(ids);

        // Remove o lock quando a conexão for resolvida
        this.connectionPromise.finally(() => {
            this.connectionLock = false;
            this.connectionPromise = null;
        });

        return this.connectionPromise;
    }

    /**
     * Verifica se o usuário está autenticado
     */
    private isUserAuthenticated(ids: UserIds): boolean {
        // Verificar se há token válido
        const token = localStorage.getItem('@gmt:access_token');
        if (!token) {
            console.warn('Token de autenticação não encontrado');
            return false;
        }

        // Verificar se o userId está presente
        if (!ids.userId) {
            console.warn('UserID não fornecido');
            return false;
        }

        // Verificar se o token não está expirado (opcional)
        try {
            const tokenData = JSON.parse(atob(token.split('.')[1]));
            const currentTime = Date.now() / 1000;
            if (tokenData.exp && tokenData.exp < currentTime) {
                console.warn('Token expirado');
                return false;
            }
        } catch (error) {
            console.warn('Erro ao verificar token:', error);
            return false;
        }

        return true;
    }

    private isConnectedWithSameIds(ids: UserIds): boolean {
        return this.socket?.connected === true &&
            this.currentIds?.userId === ids.userId &&
            this.currentIds?.customerId === ids.customerId &&
            this.currentIds?.partnerId === ids.partnerId;
    }

    private createConnection(ids: UserIds): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            try {
                console.log('Iniciando nova conexão WebSocket...');
                this.updateConnectionState({ connecting: true, error: null });

                // Desconecta socket existente se houver
                this.cleanupSocket();

                this.currentIds = ids;
                this.referenceCount++;
                this.reconnectAttempts = 0;

                const token = localStorage.getItem('@gmt:access_token');
                if (!token) {
                    throw new Error('Token de autenticação não encontrado');
                }

                // Configuração otimizada do Socket.IO
                this.socket = io(API_PATH, {
                    transports: ['websocket'],
                    query: {
                        userId: ids.userId,
                        customerId: ids.customerId,
                        partnerId: ids.partnerId
                    },
                    reconnection: false, // Controle manual de reconexão
                    reconnectionAttempts: 0,
                    reconnectionDelay: 1000,
                    timeout: this.connectionTimeout,
                    forceNew: true,
                    auth: { token },
                    autoConnect: false // Conecta manualmente
                });

                this.setupSocketListeners();
                this.setupConnectionTimeout(resolve, reject);

                // Conecta manualmente
                this.socket.connect();

            } catch (error) {
                this.handleConnectionError(error, reject);
            }
        });
    }

    private setupConnectionTimeout(resolve: () => void, reject: (error: any) => void) {
        const timeout = setTimeout(() => {
            if (!this.socket?.connected) {
                this.handleConnectionError(new Error('Timeout na conexão WebSocket'), reject);
            }
        }, this.connectionTimeout);

        // Limpa timeout quando conexão é estabelecida
        this.socket?.once('connect', () => {
            clearTimeout(timeout);
            this.updateConnectionState({
                connected: true,
                connecting: false,
                error: null,
                lastConnected: new Date()
            });
            this.startHeartbeat();
            console.log('Conexão WebSocket estabelecida com sucesso');
            resolve();
        });
    }

    private handleConnectionError(error: any, reject: (error: any) => void) {
        console.error('Erro na conexão WebSocket:', error);
        this.updateConnectionState({
            connected: false,
            connecting: false,
            error: error.message
        });
        this.cleanupSocket();
        reject(error);
    }

    private setupSocketListeners() {
        if (!this.socket) return;

        // Remove listeners existentes
        this.socket.removeAllListeners();

        // Configura listeners com tratamento de erro
        this.socket.on('connect', () => {
            console.log('WebSocket conectado com sucesso:', this.socket?.id);
            this.reconnectAttempts = 0;
            this.updateConnectionState({
                connected: true,
                connecting: false,
                error: null,
                lastConnected: new Date()
            });
        });

        this.socket.on('disconnect', (reason) => {
            console.log('WebSocket desconectado:', reason);
            this.updateConnectionState({
                connected: false,
                connecting: false,
                error: reason === 'io server disconnect' ? 'Desconectado pelo servidor' : null
            });

            this.stopHeartbeat();

            // Reconexão inteligente apenas se não foi desconexão manual
            if (reason !== 'io client disconnect' && this.reconnectAttempts < this.maxReconnectAttempts) {
                this.scheduleReconnect();
            }
        });

        this.socket.on('connect_error', (error) => {
            console.error('Erro de conexão WebSocket:', error);
            this.updateConnectionState({
                connected: false,
                connecting: false,
                error: error.message
            });
        });

        // Listeners de eventos específicos
        this.socket.on('newNotification', (notification) => {
            console.log('Nova notificação recebida:', notification);
            this.notificationSubject.next(notification);
        });

        this.socket.on('reminderDue', (reminderData: ContactReminder) => {
            console.log('Lembrete vencido recebido:', reminderData);
            this.reminderSubject.next(reminderData);
        });

        this.socket.on('newChatMessage', (chatMessage: ChatMessage) => {
            console.log('Nova mensagem de chat recebida:', chatMessage);
            this.chatMessageSubject.next(chatMessage);
        });

        // Listeners de confirmação
        this.socket.on('chatRoomJoined', (data) => {
            console.log('Confirmado: entrou na sala de chat:', data);
        });

        this.socket.on('chatRoomLeft', (data) => {
            console.log('Confirmado: saiu da sala de chat:', data);
        });

        this.socket.on('error', (error) => {
            console.error('Erro no WebSocket:', error);
            this.updateConnectionState({
                connected: false,
                connecting: false,
                error: error.message
            });
        });
    }

    private scheduleReconnect() {
        this.reconnectAttempts++;
        const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), this.maxReconnectDelay);

        console.log(`Agendando reconexão em ${delay}ms (tentativa ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

        setTimeout(() => {
            if (this.currentIds && !this.socket?.connected && !this.isDestroyed) {
                this.reconnect();
            }
        }, delay);
    }

    private startHeartbeat() {
        this.stopHeartbeat();
        this.heartbeatInterval = setInterval(() => {
            if (this.socket?.connected) {
                this.socket.emit('ping');
            }
        }, 30000); // Ping a cada 30 segundos
    }

    private stopHeartbeat() {
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
        }
    }

    private cleanupSocket() {
        this.stopHeartbeat();

        if (this.socket) {
            // Remove todos os listeners
            this.socket.removeAllListeners();

            // Desconecta se estiver conectado
            if (this.socket.connected) {
                this.socket.disconnect();
            }

            this.socket = null;
        }
    }

    private updateConnectionState(update: Partial<ConnectionState>) {
        const currentState = this.connectionStateSubject.value;
        this.connectionStateSubject.next({ ...currentState, ...update });
    }

    // Métodos públicos
    listenForNotifications(): Observable<any> {
        return this.notificationSubject.asObservable();
    }

    listenForReminders(): Observable<ContactReminder> {
        return this.reminderSubject.asObservable();
    }

    listenForChatMessages(): Observable<ChatMessage> {
        return this.chatMessageSubject.asObservable();
    }

    getConnectionState(): Observable<ConnectionState> {
        return this.connectionStateSubject.asObservable();
    }

    joinChatRoom(serviceOrderId: string) {
        if (this.socket?.connected) {
            this.socket.emit('joinChatRoom', { serviceOrderId });
            console.log(`Entrou na sala de chat: ${serviceOrderId}`);
        } else {
            console.warn('Socket não está conectado para entrar na sala de chat');
        }
    }

    leaveChatRoom(serviceOrderId: string) {
        if (this.socket?.connected) {
            this.socket.emit('leaveChatRoom', { serviceOrderId });
            console.log(`Saiu da sala de chat: ${serviceOrderId}`);
        } else {
            console.warn('Socket não está conectado para sair da sala de chat');
        }
    }

    sendMessage(event: string, data: any) {
        if (this.socket?.connected) {
            this.socket.emit(event, data);
        } else {
            console.warn('Socket não está conectado para enviar mensagem');
        }
    }

    disconnect() {
        this.referenceCount--;
        console.log(`Disconnect chamado. Referências restantes: ${this.referenceCount}`);

        if (this.referenceCount <= 0) {
            this.forceDisconnect();
        } else {
            console.log(`WebSocket ainda tem ${this.referenceCount} referências ativas.`);
        }
    }

    forceDisconnect() {
        console.log('Forçando desconexão do WebSocket...');
        this.referenceCount = 0;
        this.cleanupSocket();
        this.currentIds = undefined;
        this.reconnectAttempts = 0;
        this.connectionLock = false;
        this.lastConnectionAttempt = 0;
        this.updateConnectionState({
            connected: false,
            connecting: false,
            error: null,
            lastConnected: null
        });
        console.log('WebSocket desconectado manualmente.');
    }

    reconnect() {
        if (this.currentIds && !this.socket?.connected && !this.isDestroyed && this.reconnectAttempts < this.maxReconnectAttempts) {
            console.log('Tentando reconectar WebSocket...');
            this.setupSocketConnection(this.currentIds).catch((error) => {
                console.error('Erro na reconexão:', error);
            });
        }
    }

    isConnected(): boolean {
        return this.socket?.connected || false;
    }

    getReferenceCount(): number {
        return this.referenceCount;
    }

    getStats() {
        return {
            connected: this.socket?.connected || false,
            socketId: this.socket?.id,
            referenceCount: this.referenceCount,
            reconnectAttempts: this.reconnectAttempts,
            currentIds: this.currentIds,
            connectionLock: this.connectionLock,
            lastConnectionAttempt: this.lastConnectionAttempt
        };
    }

    // Método para destruir o serviço (útil para testes)
    destroy() {
        this.isDestroyed = true;
        this.forceDisconnect();
        this.notificationSubject.complete();
        this.reminderSubject.complete();
        this.chatMessageSubject.complete();
        this.connectionStateSubject.complete();
    }
}
